<template>
    <div>
        <!-- 기본 정보 -->
        <div class="row form-group m-t-10">
            <div class="col-md-12">
                <label>
                    <h6>
                        <trans>항목명</trans>
                    </h6>
                </label>
                <valid-input
                    :inputType="'text'"
                    :inputValue.sync="copySetting.title"
                    :rules="rules.DASHBOARD_WIDGET_ITEM_NAME_RULE"
                    :classList="'form-control'"
                    :NonStatus="true"
                />
            </div>
        </div>
        <hr />

        <!-- 데이터 설정 -->
        <div class="row form-group">
            <div class="col-md-12">
                <label>
                    <h6>
                        <trans>데이터 설정</trans>
                    </h6>
                </label>
            </div>
        </div>
        <div class="row form-group" @click.stop @mousedown.stop>
            <div class="col-md-12">
                <label>
                    <trans>계약</trans>
                </label>
                <select class="form-control" v-model="copySetting.contractIdx" @click.stop @mousedown.stop>
                    <option :value="null">{{ $t("전체 계약") }}</option>
                    <option v-for="contract in contractList" :key="contract.contractIdx" :value="contract.contractIdx">
                        {{ contract.contractName }}
                    </option>
                </select>
            </div>
        </div>
        <hr />

        <!-- 시각적 설정 -->
        <div class="row form-group">
            <div class="col-md-12">
                <label>
                    <h6>
                        <trans>스타일</trans>
                    </h6>
                </label>
            </div>
        </div>
        <div class="row form-group">
            <div class="col-md-6">
                <label>
                    <trans>배경 색상</trans>
                </label>
                <energy-color-palette v-model="copySetting.card.backgroundColor" energy-type="renewable" />
            </div>
            <div class="col-md-6">
                <label>
                    <trans>아이콘</trans>
                </label>
                <icon-select :key="iconDataKey" v-model="iconData" icon-type="both" @change="onIconChange" />
            </div>
        </div>
        <hr />

        <!-- 모달 푸터 -->
        <div class="row m-b-10">
            <div class="col-md-12 p-0">
                <div slot="modal-footer" class="w-100">
                    <button class="btn btn-primary m-r-5 pull-right" @click="onModalOK">
                        <trans>확인</trans>
                    </button>
                    <button class="btn btn-secondary pull-right m-r-10" @click="onModalCancel">
                        <trans>취소</trans>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import xeBaseSetting from "@src/views/widget/v1/common/xeBaseSetting.js";
import EnergyColorPalette from "@src/components/colorpalette/EnergyColorPalette.vue";
import IconSelect from "@src/plugins/iconSelect/IconSelect.vue";
import * as rules from "@src/consts/ruleConsts.js";
import * as validMessage from "@src/consts/validMessageConsts.js";

// 상수 정의
const MATERIAL_ICON_PATTERN = /material-icons[^\s]*/i;
const MATERIAL_ICON_PREFIX = "material";
const DEFAULT_MATERIAL_CLASS = "material-icons";

export default {
    name: "xeCostCardSetting",
    extends: xeBaseSetting,
    components: {
        EnergyColorPalette,
        IconSelect,
    },
    data() {
        return {
            contractList: [],
            iconData: null,
            iconDataKey: 0,
            rules,
            validMessage,
        };
    },
    watch: {
        "copySetting.bgIcon": {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.$nextTick(() => {
                        this.initIconData();
                    });
                }
            },
        },
    },
    mounted() {
        this.initializeComponent();
    },
    methods: {
        /**
         * 컴포넌트 초기화
         */
        async initializeComponent() {
            await this.getContractList();
            this.normalizeContractIdx();
            this.$nextTick(() => {
                this.initIconData();
            });
        },
        /**
         * 계약 목록 조회
         */
        async getContractList() {
            try {
                const { data } = await backEndApi.energyBillCalc.getContractList();
                this.contractList = Array.isArray(data) ? data : [];
            } catch (error) {
                console.error("계약 목록 조회 실패:", error);
                this.contractList = [];
            }
        },
        /**
         * contractIdx 정규화 (빈 문자열을 null로 변환)
         */
        normalizeContractIdx() {
            if (!this.copySetting.contractIdx || this.copySetting.contractIdx === "") {
                this.copySetting.contractIdx = null;
            }
        },
        /**
         * 아이콘 데이터 초기화
         * bgIcon 문자열을 IconSelect가 사용하는 iconData 객체로 변환
         */
        initIconData() {
            const bgIcon = this.copySetting.bgIcon?.trim();
            if (!bgIcon) {
                this.iconData = null;
                return;
            }

            if (this.isMaterialIcon(bgIcon)) {
                this.iconData = this.parseMaterialIcon(bgIcon);
            } else {
                this.iconData = this.parseFontAwesomeIcon(bgIcon);
            }

            if (this.iconData) {
                this.iconDataKey += 1;
            }
        },
        /**
         * Material Icon인지 확인
         */
        isMaterialIcon(iconString) {
            return iconString.includes(MATERIAL_ICON_PREFIX);
        },
        /**
         * Material Icon 파싱
         */
        parseMaterialIcon(iconString) {
            const materialClassMatch = iconString.match(MATERIAL_ICON_PATTERN);
            const materialClass = materialClassMatch ? materialClassMatch[0] : DEFAULT_MATERIAL_CLASS;
            const iconName = iconString.replace(MATERIAL_ICON_PATTERN, "").trim();

            if (!iconName) return null;

            return {
                title: iconName,
                materialClass,
            };
        },
        /**
         * FontAwesome Icon 파싱
         */
        parseFontAwesomeIcon(iconString) {
            return {
                title: iconString,
                icon: iconString,
            };
        },
        /**
         * 아이콘 변경 핸들러
         * iconData 객체를 bgIcon 문자열로 변환
         */
        onIconChange(iconData) {
            if (!iconData) {
                this.copySetting.bgIcon = "";
                return;
            }

            if (iconData.materialClass) {
                this.copySetting.bgIcon = `${iconData.materialClass} ${iconData.title}`;
            } else if (iconData.icon) {
                this.copySetting.bgIcon = iconData.icon;
            } else {
                this.copySetting.bgIcon = "";
            }
        },
        /**
         * 모달 확인 핸들러
         */
        onModalOK() {
            this.$emit("on-modal-ok", this.copySetting);
        },
        /**
         * 모달 취소 핸들러
         */
        onModalCancel() {
            this.copySetting = JSON.parse(JSON.stringify(this.initSetting));
            this.$emit("on-modal-cancel");
        },
    },
};
</script>
<style scoped>
.form-group {
    margin-bottom: 0px !important;
}
</style>
